import { ref } from 'vue';

/**
 * Audio-only Agora integration point, interface-compatible with
 * useRealtimeKitAudio so the call page and widget overlay can swap between the
 * two on whatever engine the call was created with.
 *
 * Unlike RealtimeKit, Agora renders remote audio itself — `audioTrack.play()`
 * creates and owns the audio element — so there is no remote-audio helper here.
 */

// ~500KB gzipped, and the widget loads on every page of a customer's site.
// Importing it only once a call is actually being connected keeps it out of the
// initial bundle for the vast majority of sessions that never place one.
let AgoraRTC = null;
const loadSdk = async () => {
  AgoraRTC ||= (await import('agora-rtc-sdk-ng')).default;
  return AgoraRTC;
};

export const useAgoraAudio = () => {
  const state = ref('idle');
  const errorMessage = ref('');
  const muted = ref(false);
  const remotePeerName = ref(null);
  let client = null;
  let microphoneTrack = null;

  const bindEvents = boundClient => {
    boundClient.on('user-published', async (user, mediaType) => {
      if (client !== boundClient || mediaType !== 'audio') return;
      await boundClient.subscribe(user, mediaType);
      user.audioTrack?.play();
      // Agora identifies peers by uid only; the display name comes from the
      // surrounding UI, so surface presence rather than an invented name.
      remotePeerName.value = String(user.uid);
    });
    boundClient.on('user-left', () => {
      if (client !== boundClient) return;
      if (boundClient.remoteUsers.length > 0) return;
      remotePeerName.value = null;
    });
  };

  const cleanup = async () => {
    // Closing the mic track releases the device; leaving without it keeps the
    // browser's recording indicator lit after the call has ended.
    microphoneTrack?.close();
    microphoneTrack = null;
    if (client) {
      try {
        await client.leave();
      } catch {
        // Best-effort: leaving a dead session should never block cleanup or
        // surface an error to the caller.
      }
    }
    client = null;
    remotePeerName.value = null;
    muted.value = false;
  };

  const connect = async ({ app_id: appId, channel, token, uid }) => {
    if (client) await cleanup();
    errorMessage.value = '';
    state.value = 'connecting';
    try {
      const sdk = await loadSdk();
      client = sdk.createClient({ mode: 'rtc', codec: 'vp8' });
      bindEvents(client);
      await client.join(appId, channel, token, uid);
      microphoneTrack = await sdk.createMicrophoneAudioTrack();
      await client.publish([microphoneTrack]);
      state.value = 'connected';
    } catch (error) {
      await cleanup();
      errorMessage.value = error.message;
      state.value = 'error';
      throw error;
    }
  };

  const disconnect = async () => {
    await cleanup();
    state.value = 'ended';
  };

  // setMuted keeps the track published, so the peer sees a muted participant
  // rather than one who dropped their audio entirely.
  const toggleMute = async () => {
    if (!microphoneTrack) return;
    await microphoneTrack.setMuted(!muted.value);
    muted.value = !muted.value;
  };

  return {
    state,
    errorMessage,
    muted,
    remotePeerName,
    connect,
    disconnect,
    toggleMute,
  };
};
